import { memo, useState } from 'react'
import { AnimatePresence, motion, Transition } from 'framer-motion'
import { logAmplitudeEvent } from '../../../../utils'

import { InputAttachments } from './input-attachments'
import { WindowContext } from './window-context'
import { WebviewContext, OptimisticWebviewContext } from './webview-context'
import { useIsCurrentWindowAttached, useResetChatContext, useSelectedWindows } from '../../../../store/atoms'
import { useAttachments } from '../../../../store'
import { useWebviewContext, useOptimisticWebviewContext } from '../../../../store/webview'
import { Tooltip } from '@highlight/ui'
import { PlusIcon } from '../../../icons'
import { useContextBarMenuOpen, useSetContextBarMenuOpen } from './context-bar-wrapper'
import { useHasOpenPreviews } from './previewable-context'
import { isElectron } from '../../../../utils/runtimeUtils'
import { twMerge } from 'tailwind-merge'
import { RotateCw } from 'lucide-react'

export const ContextBar = memo(() => {
  const isCurrentWindowAttached = useIsCurrentWindowAttached()
  const isMenuOpen = useContextBarMenuOpen()
  const setMenuOpen = useSetContextBarMenuOpen()
  const hasOpenPreviews = useHasOpenPreviews()
  const attachments = useAttachments()
  const webviewContext = useWebviewContext()
  const optimisticWebviewContext = useOptimisticWebviewContext()
  const resetContext = useResetChatContext()
  const selectedWindows = useSelectedWindows()

  // local state
  const [colors] = useState<string[]>()

  const hasAnyContext =
    attachments.length > 0 ||
    !!webviewContext ||
    !!optimisticWebviewContext ||
    isCurrentWindowAttached ||
    selectedWindows.length > 0

  // use as the first attachment if there are no other attachments exist
  const hasOtherAttachments = attachments.length > 0
  const showWebviewFirst = webviewContext && !hasOtherAttachments
  const showOptimisticWebviewFirst = !webviewContext && optimisticWebviewContext && !hasOtherAttachments

  // === FRAMER MOTION ANIMATION ===
  // We use CSS variables for the two gradient stops, so Framer Motion can interpolate from transparent.
  return (
    <>
      {/* @ts-ignore */}
      <motion.div
        // @ts-ignore
        initial={
          {
            '--start-color': 'rgba(0,0,0,0)',
            '--end-color': 'rgba(0,0,0,0)',
          } as React.CSSProperties
        }
        // @ts-ignore
        animate={
          {
            '--start-color': isCurrentWindowAttached ? 'rgba(0,0,0,0)' : 'rgba(0,0,0,0)',
            '--end-color': isCurrentWindowAttached ? (colors?.[1] ?? 'rgba(0,0,0,0)') : 'rgba(0,0,0,0)',
          } as React.CSSProperties
        }
        transition={containerTransition}
        className={twMerge(
          'group/container relative flex h-full w-full cursor-pointer select-none items-center justify-between overflow-hidden',
          // Room below for the pills' shadow, which is their visible edge and overflow-hidden would clip.
          'p-2.5 pb-1 transition-[background]',
          'bg-[linear-gradient(to_right,var(--start-color),var(--end-color))]',
        )}
      >
        <div className={'flex flex-wrap items-center gap-0.5'}>
          {showWebviewFirst && <WebviewContext />}
          {showOptimisticWebviewFirst && <OptimisticWebviewContext />}
          {isElectron && <WindowContext />}
          {!showWebviewFirst && !showOptimisticWebviewFirst && <WebviewContext />}
          {!showWebviewFirst && !showOptimisticWebviewFirst && <OptimisticWebviewContext />}
          <InputAttachments />
          <Tooltip disabled={isMenuOpen || hasOpenPreviews}>
            <Tooltip.Trigger
              render={
                <motion.div
                  layout={'position'}
                  transition={plusTransition}
                  className={twMerge(
                    'text-foreground-placeholder group-hover/container:text-foreground hover:text-foreground hover:bg-element-hover flex h-7 items-center justify-center rounded-[10px] border transition-colors',
                    !isElectron ? 'gap-2 px-2' : 'w-7',
                  )}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation()
                    setMenuOpen((prev) => !prev)
                    logAmplitudeEvent('Open Attachments Menu', {
                      trigger: 'Add Button',
                    })
                  }}
                />
              }
            >
              <PlusIcon size={12} />
              {!isElectron && <span className={'leading-none'}>Add Attachment</span>}
            </Tooltip.Trigger>
            <Tooltip.Content>Add Attachments</Tooltip.Content>
          </Tooltip>
          <AnimatePresence>
            {hasAnyContext && (
              <Tooltip disabled={isMenuOpen || hasOpenPreviews}>
                <Tooltip.Trigger
                  render={
                    <motion.div
                      layout={'position'}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={plusTransition}
                      className="text-foreground-placeholder group-hover/container:text-foreground hover:text-foreground hover:bg-element-hover flex h-7 w-7 items-center justify-center rounded-[10px] border transition-colors"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation()
                        resetContext()
                        logAmplitudeEvent('Clear Attachments', {
                          trigger: 'Clear Button',
                        })
                      }}
                    />
                  }
                >
                  <RotateCw size={14} />
                </Tooltip.Trigger>
                <Tooltip.Content>Clear Attachments</Tooltip.Content>
              </Tooltip>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </>
  )
})

const containerTransition: Transition = {
  duration: 0.2,
  ease: 'easeInOut',
}

const plusTransition: Transition = { duration: 0.2 }
